'use client';

import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from '@/hook';

import { useAnalytics } from '@/integrations';

import CarSoldOverlay from '@/components/car-details//CarProfileNew/SimilarCars/CarSoldOverlay';
import { DisplayedCarInfoInterface, LenderWithInterestRates } from '@/types';

import SliderUnControlled from '@/components/shared/SliderUncontrolled';
import Typography from '@/components/shared/typography/typography.component';

import { useLoanPlans } from '@/hook/use-loan-plans.hook';
import Button from '@/components/shared/Button/button.component';
import { numberWithCommas } from '@/utils';
import Image from 'next/image';
import DownloadAppBottomSheet from '@/components/download-app-bottom-sheet';
import { SylndrSwift } from '../../../../public/Images/financing';
import { Box } from '@mui/material';
import useAuth from '@/hook/use-auth.hook';

const defaultDownpayment = 50;
const defaultMonths = 60;

function CalculatorPoweredBy(props: { s: string }) {
    return (
        <div
            className={
                'flex flex-row items-center gap-1 justify-center w-full mt-2'
            }
        >
            <Typography variant={'caption'} className={'text-gray-600'}>
                {props.s}
            </Typography>
            <Image src={SylndrSwift} width={66} alt={'sylndr-swift'} />
        </div>
    );
}

const FinanceCalculatorNew: FC<{
    carPrice: number;
    canApply?: boolean;
    lenders?: LenderWithInterestRates[];
    otherPartners?: LenderWithInterestRates[];
    eventName?: string;
    carName?: string;
    isSold?: boolean;
    isPurchaseInProgress?: boolean;
    carInfo: DisplayedCarInfoInterface;
    mobileButtons?: JSX.Element;
    showOtherPartners?: boolean;
}> = ({
    carPrice,
    lenders,
    eventName,
    carName,
    isSold,
    carInfo,
    otherPartners,
    // mobileButtons,
    isPurchaseInProgress,
    showOtherPartners = true,
}) => {
    const [userInteracted, setUserInteracted] = useState(false);
    const { t } = useTranslation();
    const { trackEvent } = useAnalytics();
    const { userId, phone: mobileNumber, name: userName, email } = useAuth();

    const [bottomSheetOpen, setBottomSheetOpen] = useState(false);

    const openApp = () => {
        window.location.href = `sylndrapp://Financing-flow/FinancingSylndrOnboarding?carID=${carInfo.id}`;
        trackEvent('Action: Swift - Apply CTA', {
            userId,
            mobileNumber,
            userName,
            email,
            sourceButton: 'Financing Calculator CTA in Car Profile',
        });
        setTimeout(() => {
            openBottomSheet();
        }, 500);
    };

    const openBottomSheet = () => {
        setBottomSheetOpen(true);
    };
    const {
        currentPlan,
        tenor,
        setTenor,
        downPaymentPercent,
        setDownPaymentPercent,
    } = useLoanPlans({
        carPrice,
        startDate: useMemo(() => new Date(), []), // Avoid recalculating on every render
        interestRate: lenders?.[0]?.interestRate[0].interestRate || 33.5,
    });

    const minDownPayment = 20;
    const maxDownPayment = 95;

    const downPaymentInput = Math.round(
        carPrice * (downPaymentPercent / 100)
    ).toLocaleString('en-US');

    const handleDownPaymentInput = useCallback(
        (val: string) => {
            const sanitizedValue = val.replace(/[^\d]/g, '');

            const numericValue = parseInt(sanitizedValue, 10);

            if (!isNaN(numericValue)) {
                setDownPaymentPercent((numericValue / carPrice) * 100);
            } else {
                setDownPaymentPercent(0);
            }
        },
        [carPrice, minDownPayment, downPaymentPercent]
    );
    const handleSliderChange = useCallback(
        (value: number) => {
            setDownPaymentPercent(value);
        },
        [carPrice]
    );

    const paymentPerMonth = currentPlan?.installmentAmountWithoutInsurance;
    const range = (start, stop, step) =>
        Array.from(
            { length: Math.ceil((stop - start) / step) },
            (_, i) => start + i * step
        );
    const fireFinancingEvent = () => {
        if (!userInteracted) {
            trackEvent(eventName || 'Action: Thank You Financing', {
                interaction: true,
                carName: carName,
                isRefurbished: carInfo?.isRefurbished,
            });

            setUserInteracted(true);
        }
    };

    const disabled = downPaymentPercent > 95 || downPaymentPercent < 20;
    return (
        <div className={'pt-5 pb-2 '}>
            <DownloadAppBottomSheet
                open={bottomSheetOpen}
                setOpen={setBottomSheetOpen}
                source="Financing Calculator CTA in Car Profile"
            />
            <div
                className={
                    'border border-gray-200 rounded-xl overflow-hidden relative flex flex-col md:flex-row-reverse items-stretch '
                }
            >
                <div className="relative shadow-[0px_-8px_20px_0px_rgba(0,155,93,0.08)]    md:pb-4 flex flex-col justify-center">
                    <div
                        className={
                            'py-6 flex flex-col items-center gap-3 px-4 z-1 justify-center h-full'
                        }
                    >
                        <div className={'flex flex-col items-center gap-1'}>
                            <Typography
                                variant={'caption'}
                                className={'text-gray-600 text-center'}
                            >
                                {t('Monthly Installments Starting From')}
                            </Typography>
                            <Typography
                                variant={'h6SemiBold'}
                                className={'text-primary-700'}
                            >
                                {!disabled
                                    ? `${t('EGP')} ${numberWithCommas(
                                          paymentPerMonth
                                      )}`
                                    : '-'}
                            </Typography>
                        </div>
                        <Button
                            disabled={disabled}
                            className={'w-1/2'}
                            onClick={openApp}
                        >
                            <Typography variant={'captionMedium'}>
                                {t('Apply Financing with this EMI')}
                            </Typography>
                        </Button>
                    </div>
                    <div className={'hidden md:flex mt-auto'}>
                        <CalculatorPoweredBy s={t('CalculatorPoweredBy')} />
                    </div>
                    <div
                        className={
                            'bg-financing-gradient-dark md:bg-financing-gradient-dark-desktop opacity-30  absolute w-full h-full top-0 left-0 z-0 pointer-events-none '
                        }
                    />
                </div>
                {isSold || isPurchaseInProgress ? (
                    <>
                        {/* eslint-disable-next-line react/jsx-no-undef */}
                        <Box sx={{ my: '80px' }} />
                        <Button />
                        <Box sx={{ my: '40px' }} />
                        <CarSoldOverlay
                            style={{ paddingTop: '0px' }}
                            type={
                                isPurchaseInProgress
                                    ? 'PURCHASE_IN_PROGRESS'
                                    : 'SOLD'
                            }
                        />
                    </>
                ) : (
                    <div
                        className={
                            'flex flex-col px-4 gap-8 pb-8 pt-2 md:pt-8 md:px-6 w-full'
                        }
                    >
                        <div
                            className={
                                'flex flex-row justify-between items-center w-full '
                            }
                        >
                            <Typography
                                variant={'body2Medium'}
                                className={'text-gray-700'}
                            >
                                {t('Car Price')}
                            </Typography>
                            <Typography
                                variant={'body2'}
                                className={'text-gray-900'}
                            >
                                {`${t('EGP')} ${numberWithCommas(carPrice)}`}
                            </Typography>
                        </div>

                        <div className="w-full flex-col">
                            <div className="flex-row flex items-center justify-between pb-1.5">
                                <Typography
                                    variant={'body2Medium'}
                                    className="text-gray-700"
                                >
                                    {t('Downpayment')}
                                </Typography>
                                <div className="bg-gray-100 rounded-lg flex flex-row items-center ">
                                    <Typography
                                        variant={'labelMedium'}
                                        className=" mx-2"
                                    >
                                        {Math.round(downPaymentPercent).toFixed(
                                            0
                                        )}
                                        %
                                    </Typography>
                                    <div
                                        className={
                                            'h-10 w-[50] px-4 text-center border rounded-md border-gray-200 justify-center flex flex-row bg-white items-center gap-1'
                                        }
                                    >
                                        <Typography variant={'body2'}>
                                            {t('EGP')}
                                        </Typography>
                                        <input
                                            min={
                                                (minDownPayment * carPrice) /
                                                100
                                            }
                                            max={
                                                (maxDownPayment * carPrice) /
                                                100
                                            }
                                            className="w-24 border-0 outline-0 text-sm"
                                            value={downPaymentInput}
                                            type="number-pad"
                                            onChange={e =>
                                                handleDownPaymentInput(
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                            <SliderUnControlled
                                value={downPaymentPercent}
                                aria-label="Downpayment"
                                defaultValue={defaultDownpayment}
                                valueLabelDisplay="off"
                                step={5}
                                marks={range(
                                    minDownPayment,
                                    maxDownPayment + 5,
                                    5
                                ).map(v => {
                                    return (
                                        v % 10 == 0 && {
                                            label: `${v}%`,
                                            value: v,
                                        }
                                    );
                                })}
                                sliderLabel={false}
                                max={maxDownPayment}
                                min={minDownPayment}
                                onChange={(_, value) => {
                                    fireFinancingEvent();
                                    handleSliderChange(value as number);
                                    // setDownpayment(value as number);
                                }}
                            />
                        </div>
                        <div>
                            <div
                                className={
                                    'flex flex-row justify-between items-center w-full '
                                }
                            >
                                <Typography
                                    variant={'body2Medium'}
                                    className={'text-gray-700'}
                                >
                                    {t('Duration')}
                                </Typography>
                                <Typography
                                    variant={'captionSemiBold'}
                                    className={'text-gray-800'}
                                >
                                    {`${tenor} ${t('Months')}`}
                                </Typography>
                            </div>

                            <SliderUnControlled
                                value={tenor}
                                aria-label="Months"
                                defaultValue={defaultMonths}
                                valueLabelDisplay="off"
                                step={6}
                                marks={range(6, 76, 6).map(v => {
                                    return { value: v, label: v };
                                })}
                                min={6}
                                max={72}
                                onChange={(_, value) => {
                                    fireFinancingEvent();
                                    setTenor(value as number);
                                }}
                                valueLabelFormat={value => (
                                    <Typography variant={'captionMedium'}>
                                        {value + ' ' + t('Months')}
                                    </Typography>
                                )}
                            />
                        </div>
                    </div>
                )}
            </div>
            <div className={'md:hidden'}>
                <CalculatorPoweredBy s={t('CalculatorPoweredBy')} />
            </div>
            {showOtherPartners && otherPartners && otherPartners.length > 0 && (
                <div
                    className={
                        'py-6 w-full justify-between flex flex-col md:flex-row gap-3'
                    }
                >
                    <div>
                        <Typography
                            variant={'subtitle2SemiBold'}
                            className={'text-secondary-500'}
                        >
                            {t('otherPartnersTitle')}
                        </Typography>
                        <Typography
                            variant={'caption'}
                            className={'text-gray-500'}
                        >
                            {t('otherPartnersDescription')}
                        </Typography>
                    </div>

                    <div className={'flex flex-row gap-3 w-full md:w-1/3'}>
                        {otherPartners.map(v => {
                            return (
                                <div
                                    key={v.id}
                                    className={
                                        'px-2 flex flex-row items-center justify-center rounded-md py-3.5 shadow-[0px_0px_10px_0px_rgba(0,0,0,0.04)] w-full '
                                    }
                                >
                                    <Image
                                        src={v.logo}
                                        width={60}
                                        height={15}
                                        alt={v.id}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FinanceCalculatorNew;
